/**
 * @file AddressSearch component
 *
 * Will implement teh Address Search functionality
 */

import Data from '../../utils/dom/data';
import SelectorEngine from '../../utils/dom/selector-engine';
import EventHandler from '../../utils/dom/event-handler';
import Request from '../../utils/request';
import Modal from '../Modal';
import Select from '../Select';
import attachValidation, { VALID_POSTCODE_CHECK, isGreatBritainPostcode, GB_ADDRESS_ERROR_MESSAGE } from '../../utils/form-validation';
import { ClassNames } from '../../utils/dom/css-classes';
import { getDataFromForm } from '../../utils/misc';
import BaseComponent from '../BaseComponent';

const IS_LOCAL = false;
const NAME = 'AddressSearch';
const DATA_KEY = 'lta.addresssearch';
const EVENT_KEY = `.${DATA_KEY}`;
const DATA_API_KEY = '.data-api';

const EVENT_CLICK_DATA_API = `click${EVENT_KEY}${DATA_API_KEY}`;
export const EVENT_EDIT_ADDRESS_MANUALLY = 'EVENT_EDIT_ADDRESS_SEARCH';
export const EVENT_EDIT_ADDRESS_SEARCH = 'EVENT_EDIT_ADDRESS_SEARCH';

const SELECTOR_DATA_TOGGLE = '[data-lta-addresssearch]';
const SELECTOR_HOUSE_INPUT = 'houseNumber';

class AddressSearch extends BaseComponent {
	constructor(element, config = {}) {
		super(element);
		this.state = {
			address: undefined
		};
		this._element = element;
		this._restrictToGB = Boolean(config?.restrictToGB);
		this._gbAddressErrorMessage = config?.gbAddressErrorMessage || GB_ADDRESS_ERROR_MESSAGE;
		this._modal = undefined;
		this._select = undefined;
		this._views = undefined;
		this._inView = undefined;
		this._retriveAddress = undefined;
		this._loadingError = undefined;
		this._loadingErrorCta = undefined;
		this.disbaleFocus = false;

		this._manualForm = undefined;
		this.manualFormValidationInstance = undefined;
		this._suggestForm = undefined;
		this.suggestedFormValidationInstance = undefined;
		this._changeCallbacks = [];
		this._defaultErrorLabel = undefined;

		if (this._element) {
			this.initiateSearch();

			Data.setData(this._element, DATA_KEY, this);
		}
	}

	static get NAME() {
		return NAME;
	}

	onChange(callback) {
		this._changeCallbacks.push(callback);
	}

	async initiateSearch() {
		this._modal = new Modal(SelectorEngine.findOne('#address-search-modal', this._element), { backdrop: 'static' });
		this._retriveAddress = this._element.classList.contains('js-retrive-address');

		this._views = SelectorEngine.find('.lta-address-toggle-view', this._element);
		this._inView = this._views[0];

		const toggleViewLinks = SelectorEngine.find('.js-address-toggle', this._element);
		for (const link of toggleViewLinks) {
			link.addEventListener('click', (event) => this.toggleView(event));
		}

		this._manualForm = SelectorEngine.findOne('#addressManual', this._element);
		this._suggestForm = SelectorEngine.findOne('#addressSuggest', this._element);
		const seggestPopup = SelectorEngine.findOne('#address-suggest', this._element);

		this.addManualFormValidation(this._manualForm);
		this.addSuggestedFormValidation(this._suggestForm);
		this.addPopupFormValidation(seggestPopup);

		const cancelPopupButtons = SelectorEngine.find('.js-lookup-cancel', this._element);

		for (const cancelPopup of cancelPopupButtons) {
			cancelPopup.addEventListener('click', () => {
				this.resetView();
				this._modal.hide();
			});
		}

		const manualEdit = SelectorEngine.findOne('.js-manual-edit', this._element);
		EventHandler.on(manualEdit, 'click', () => {
			EventHandler.trigger(this._element, EVENT_EDIT_ADDRESS_MANUALLY, {});
			this.handleManualEdit();
		});

		const autoEdit = SelectorEngine.findOne('.js-auto-edit', this._element);
		EventHandler.on(autoEdit, 'click', () => {
			EventHandler.trigger(this._element, EVENT_EDIT_ADDRESS_SEARCH, {});
			this.handleSearchAgain();
		});

		const errorCta = SelectorEngine.findOne('.js-dismiss-error', this._element);
		errorCta.addEventListener('click', (event) => {
			const errorWrapper = event.target.closest('.lta-address-search--error');
			errorWrapper.classList.add(ClassNames.Hidden);
			const autoSubmit = SelectorEngine.findOne('.js-auto-submit', this._element);
			autoSubmit.disabled = false;
		});

		if (this._retriveAddress) {
			this._loadingError = SelectorEngine.findOne('.lta-address-search--loading-error', this._element);
			this._loadingErrorCta = SelectorEngine.findOne('.js-dismiss-address-loading--error', this._loadingError);
			this._loadingErrorCta.addEventListener('click', () => {
				this._loadingError.classList.add(ClassNames.Hidden);
			});
			await this.getUserAddress();
		}
	}

	async getUserAddress() {
		try {
			const existingAddress = await Request.get('/api/account/address');

			if (existingAddress?.address && existingAddress?.address?.postCode) {
				this.populateManualForm(existingAddress.address, false, true);
				this.addAddress(existingAddress.address, true, true);
			}
		} catch {}
	}

	toggleView(event) {
		event.preventDefault();

		for (const view of this._views) {
			if (view.classList.contains(ClassNames.Hidden)) {
				const [firstInput] = SelectorEngine.find('input', view);
				view.classList.remove(ClassNames.Hidden);
				firstInput.focus();
				this._inView = view;
			} else {
				view.classList.add(ClassNames.Hidden);
			}
		}
	}

	changeHandler(event) {
        for (const callback of this._changeCallbacks) {
			callback(event);
		}
     }

	handleManualEdit() {
		const manualView = SelectorEngine.findOne('.lta-address-search--find-manual', this._element);
		const [firstManualInput] = SelectorEngine.find('input', manualView);
		manualView.classList.remove(ClassNames.Hidden);
		this._inView = manualView;
		firstManualInput.focus();

		const resultsView = SelectorEngine.findOne('.lta-address-search--result', this._element);
		resultsView.classList.add(ClassNames.Hidden);

		const searchView = SelectorEngine.findOne('.lta-address-search--find', this._element);
		searchView.classList.remove(ClassNames.Hidden);
		this.changeHandler('removeAddress');
	}

	handleSearchAgain() {
		const autoView = SelectorEngine.findOne('.lta-address-search--find-suggest', this._element);
		const [firstAutoInput] = SelectorEngine.find('input', autoView);
		autoView.classList.remove(ClassNames.Hidden);
		this._inView = autoView;
		if(!this.disbaleFocus) {
			firstAutoInput.focus();
		}

		const resultsView = SelectorEngine.findOne('.lta-address-search--result', this._element);
		resultsView.classList.add(ClassNames.Hidden);

		const searchView = SelectorEngine.findOne('.lta-address-search--find', this._element);
		searchView.classList.remove(ClassNames.Hidden);
		this.changeHandler('removeAddress');
	}

	resetView() {
		const manualAction = SelectorEngine.findOne('.js-manual-submit', this._element);
		const autoAction = SelectorEngine.findOne('.js-auto-submit', this._element);
		const popupAction = SelectorEngine.findOne('.js-select-address', this._element);
		const popupSelect = SelectorEngine.findOne('#autosuggestAddress', this._element);
		const selectItemsWrapper = SelectorEngine.findOne('.lta-select__items-wrapper', this._element);

		manualAction.disabled = false;
		autoAction.disabled = false;
		popupAction.disabled = false;

		const firstOption = popupSelect.item(0).cloneNode(true);
		popupSelect.textContent = '';
		popupSelect.add(firstOption);

		selectItemsWrapper.textContent = '';
	}

	getPostcodeValidators() {
		const validators = {
			notEmpty: {
				message: 'Please enter a postcode'
			},
			regexp: {
				message: 'Please enter a valid postcode',
				regexp: VALID_POSTCODE_CHECK
			}
		};

		if (this._restrictToGB) {
			validators.callback = {
				message: this._gbAddressErrorMessage,
				callback: (input) => {
					const value = typeof input === 'string' ? input : input?.value;
					if (!value) {
						return { valid: true };
					}
					const trimmed = String(value).replace(/\u00A0/g, ' ').trim();
					// Format errors are handled by the regexp validator; only reject valid NI postcodes here.
					if (!VALID_POSTCODE_CHECK.test(trimmed)) {
						return { valid: true };
					}
					return { valid: isGreatBritainPostcode(trimmed) };
				}
			};
		}

		return validators;
	}

	addManualFormValidation(form) {
		this.manualFormValidationInstance = attachValidation(form, {
			street: {
				validators: {
					notEmpty: {
						message: 'Please enter a street'
					},
					regexp: {
						message: 'Please enter a valid street',
						regexp: /^(?=.*\S)[A-Za-z0-9\s,.\-#/]{1,100}$/
					}
				}
			},
			city: {
				validators: {
					notEmpty: {
						message: 'Please enter a city'
					},
					regexp: {
						message: 'Please enter a valid city',
						regexp: /^[A-Za-z]+(?:[ -][A-Za-z]+)*$/
					}
				}
			},
			state: {
				validators: {				
					regexp: {
						message: 'Please enter a valid county',
						regexp: /^[A-Za-z]+(?:[ -][A-Za-z]+)*$/
					}
				},
			},
			postCode: {
				validators: this.getPostcodeValidators()
			}
		});

		this.manualFormValidationInstance.on('core.form.valid', () => {
			const formAction = SelectorEngine.findOne('.js-manual-submit', form);
			formAction.disabled = true;

			const dataObject = getDataFromForm(form);

			this.addAddress(dataObject, false, true);
		});
	}

	addSuggestedFormValidation(form) {
		if (!form) {
			return;
		}
		this.suggestedFormValidationInstance = attachValidation(form, {
			houseNumber: {
				validators: {
					notEmpty: {
						message: 'Please enter a House No and Street name'
					},
					regexp: {
						message: 'Please enter a valid house number and street name',
						regexp: /^[A-Za-z0-9\s,.\-#/]{1,100}$/
					}
				}
			},
			postcode: {
				validators: this.getPostcodeValidators()
			}
		});

		this.suggestedFormValidationInstance.on('core.form.valid', async () => {
			const formAction = SelectorEngine.findOne('.js-auto-submit', form);
			formAction.disabled = true;

			const dataObject = getDataFromForm(form);

			const postcodeLookup = await Request.post(form.dataset.url, dataObject, { local: IS_LOCAL }).catch(() => this.showError('api'));

			if (postcodeLookup) {
				const { AddressList, SelectedAddress } = postcodeLookup;
				if (AddressList.length === 0 && !SelectedAddress) {
					this.showError('data');
				} else {
					if (SelectedAddress) {
						this.populateManualForm(SelectedAddress);
					} else {
						this.populateSelect(AddressList);
					}
				}
			}
		});
	}

	showError(errorType) {
		const errorPopup = SelectorEngine.findOne('.lta-address-search--error', this._element);
		const errorPopupCta = SelectorEngine.findOne('.js-dismiss-error', errorPopup);
		const list = SelectorEngine.find('li', errorPopup);
		const errorLabel = SelectorEngine.findOne('.lta-snackbar__label', errorPopup);

		if (errorLabel && this._defaultErrorLabel === undefined) {
			this._defaultErrorLabel = errorLabel.textContent;
		}

		if (errorType === 'gb') {
			if (errorLabel) {
				errorLabel.textContent = this._gbAddressErrorMessage;
				errorLabel.setAttribute('aria-label', this._gbAddressErrorMessage);
			}
			for (const item of list) {
				item.classList.add(ClassNames.Hidden);
			}
		} else {
			if (errorLabel && this._defaultErrorLabel !== undefined) {
				errorLabel.textContent = this._defaultErrorLabel;
				errorLabel.setAttribute('aria-label', this._defaultErrorLabel);
			}
			if (errorType === 'api') {
				list[0].classList.add(ClassNames.Hidden);
				list[1].classList.remove(ClassNames.Hidden);
			} else {
				list[0].classList.remove(ClassNames.Hidden);
				list[1].classList.add(ClassNames.Hidden);
			}
		}
		errorPopup.classList.remove(ClassNames.Hidden);
		errorPopupCta.focus();
	}

	addPopupFormValidation(form) {
		const popupValidation = attachValidation(form, {
			moniker: {
				validators: {
					notEmpty: {
						message: 'Please select one address from the list'
					}
				}
			}
		});

		popupValidation.on('core.form.valid', async () => {
			const formAction = SelectorEngine.findOne('.js-select-address', form);
			formAction.disabled = true;

			const dataObject = getDataFromForm(form);

			const addressLookup = await Request.post(form.dataset.url, dataObject, { local: IS_LOCAL });

			this.populateManualForm(addressLookup.Address, false);
			this.resetView();
			this._modal.hide();
		});
	}

	populateSelect(addresses) {
		const modal = SelectorEngine.findOne('#address-search-modal', this._element);
		const selectWrapper = SelectorEngine.findOne('.lta-select', modal);
		const [select] = SelectorEngine.find('select', selectWrapper);

		for (const address of addresses) {
			const option = document.createElement('option');
			option.value = address.Moniker;
			const label = document.createTextNode(address.Address);
			option.append(label);
			select.append(option);
		}
		if (!this._select) {
			this._select = new Select(selectWrapper);
		} else {
			this._select.reBindSelectItems(selectWrapper);
		}
		this._modal.show();
	}

	populateManualForm(address, isFocus = true, existing = false) {
		this.addAddress(address, existing, isFocus);
		for (const [key, value] of Object.entries(address)) {
			const field = SelectorEngine.findOne(`[name='${key}']`, this._manualForm);
			if (field) field.value = value;
		}
		if(isFocus) {
			this.manualFormValidationInstance.validate();
		}
	}

	addAddress(address, existing = false, isFocus = true) {
		this.resetView();
		this._inView.classList.add(ClassNames.Hidden);
		this.state.address = address;

		const addressSummary = SelectorEngine.findOne('.lta-address-search--result', this._element);
		const addressWrapper = SelectorEngine.findOne('.lta-location-card--content', addressSummary);
		addressWrapper.textContent = '';

		const p = document.createElement('p');
		p.classList.add('u-body2');
		const streetEle = document.createTextNode(address.street);
		const cityEle = document.createTextNode(address.city);
		const stateEle = document.createTextNode(address.state);
		const postcodeEle = document.createTextNode(address.postCode);
		const countryEle = document.createTextNode('United Kingdom');

		p.append(streetEle);
		p.append(document.createElement('br'));
		p.append(cityEle);
		if (address.state !== '') {
			p.append(document.createElement('br'));
			p.append(stateEle);
		}
		p.append(document.createElement('br'));
		p.append(postcodeEle);
		if (!address.country || address.country !== '') {
			p.append(document.createElement('br'));
			p.append(countryEle);
		}

		addressWrapper.append(p);

		const hiddenInput = (name, value = "") => {
			const input = document.createElement('input');
			input.setAttribute('type', 'text');
			input.classList.add(ClassNames.Hidden);
			input.setAttribute('name', name);
			input.setAttribute('value', value);
			return input;
		};

		const streetInput = hiddenInput('street', address.street);
		const cityInput = hiddenInput('city', address.city);
		const stateInput = hiddenInput('state', address.state);
		const postCodeInput = hiddenInput('postCode', address.postCode);
		const countryInput = hiddenInput('country', 'United Kingdom');

		addressWrapper.append(streetInput);
		addressWrapper.append(cityInput);
		addressWrapper.append(stateInput);
		addressWrapper.append(postCodeInput);
		addressWrapper.append(countryInput);

		const formEvent = new CustomEvent('change', {
			bubbles: true,
			detail: { name: 'address', values: this.state.address, existing }
		});

		streetInput.dispatchEvent(formEvent);

		const isAddressMissing = !address.postCode && !address.street;

		addressSummary.classList.remove(ClassNames.Hidden);
		const [firstCta, secondCta] = SelectorEngine.find('button', addressSummary);
		if (isAddressMissing && secondCta) {
			secondCta.click();
			const input = SelectorEngine.findOne(SELECTOR_HOUSE_INPUT, this._element);
			if (isFocus && input) input.focus();
		} else {
			if(isFocus) {
				firstCta.focus();
			}
		}
		this.changeHandler('addAddress');
	}

	validate() {
		// if address has been successfully selected both views will have .u-hidden
		if (this._inView.classList.contains(ClassNames.Hidden)) {
			if (this._restrictToGB && !isGreatBritainPostcode(this.state.address?.postCode)) {
				this.showError('gb');
				return false;
			}
			return 'Valid';
		}

		if (this._inView.classList.contains('lta-address-search--find-suggest')) {
			const validationStatus = this.suggestedFormValidationInstance.validate().then((status) => status);
			return validationStatus;
		} else {
			const validationStatus = this.manualFormValidationInstance.validate().then((status) => status);
			return validationStatus;
		}
	}
}

/**
 * Data Api implementation
 */
EventHandler.on(document, EVENT_CLICK_DATA_API, SELECTOR_DATA_TOGGLE, function (event) {
	event.preventDefault();

	// eslint-disable-next-line no-invalid-this
	Data.getData(this, DATA_KEY) || AddressSearch.getOrCreateInstance(this);
});

export default AddressSearch;
